import React from 'react'

import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useAPIGetPeriodDetail } from 'api/kinerja/penilik-pegawai/period'

type PhaseGuard = {
  status: 'idle' | 'open' | 'not-started' | 'ended'
  isOpen: boolean
  startDate?: string
  endDate?: string
}

const usePPSPeriodGuard = () => {
  const { period } = usePeriodContext()

  // Get Period Detail
  const {
    data: periodData,
    isSuccess: isPeriodSuccess,
    isFetching: isPeriodFetching,
  } = useAPIGetPeriodDetail(period.id, {
    staleTime: 60 * 60 * 1000, // 1 hour
  })

  const buildPhaseGuard = React.useCallback(
    (phase?: { startDate?: string; endDate?: string }): PhaseGuard => {
      if (isPeriodFetching || !isPeriodSuccess) {
        return { status: 'idle', isOpen: true }
      }

      const startDate = phase?.startDate
      const endDate = phase?.endDate

      if (!startDate || !endDate) {
        return { status: 'idle', isOpen: true }
      }

      const start = new Date(`${startDate}T00:00:00`)
      const end = new Date(`${endDate}T23:59:59`)

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return { status: 'idle', isOpen: true }
      }

      const now = new Date()

      if (now < start) {
        return { status: 'not-started', isOpen: false, startDate, endDate }
      }

      return { status: 'open', isOpen: true, startDate, endDate }
    },
    [isPeriodFetching, isPeriodSuccess]
  )

  const perencanaanGuard = React.useMemo(
    () => buildPhaseGuard(periodData?.perencanaan),
    [buildPhaseGuard, periodData?.perencanaan]
  )
  const pelaksanaanGuard = React.useMemo(
    () => buildPhaseGuard(periodData?.pelaksanaan),
    [buildPhaseGuard, periodData?.pelaksanaan]
  )
  const penetapanGuard = React.useMemo(
    () => buildPhaseGuard(periodData?.penetapan),
    [buildPhaseGuard, periodData?.penetapan]
  )

  const renderPhaseGuardCard = React.useCallback(
    (title: string, guard: PhaseGuard) => {
      if (!guard.isOpen) return null
    },
    []
  )

  return {
    isPeriodFetching,
    isPeriodSuccess,
    perencanaanGuard,
    pelaksanaanGuard,
    penetapanGuard,
    renderPhaseGuardCard,
  }
}

export default usePPSPeriodGuard
